import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import React, { forwardRef, useRef, useState, useEffect } from "react";
import { G as Grid, B as Box, j as useResolvedAttributes } from "./Grid-CcQKdsgp.js";
import { u as useUiKitComponents } from "./useUiKitComponents-CaMIXGvd.js";
import { useMenuItem, useMenuSection, useMenuTrigger, useMenu } from "@react-aria/menu";
import { useTreeState } from "@react-stately/tree";
import { Item, Section } from "@react-stately/collections";
import { P as Popover } from "./Popover-B1B60g-t.js";
import { useButton } from "@react-aria/button";
import { useMenuTriggerState } from "@react-stately/menu";
const DefaultWrapper = ({ children, ...rest }) => /* @__PURE__ */ jsx(Grid, { gap: 16, cols: 1, ...rest, children });
const ElementType = forwardRef(({ children, ...rest }, ref) => {
  return /* @__PURE__ */ jsx("div", { ref, ...rest, children });
});
const LinkElementType = forwardRef(({ children, href, ...rest }, ref) => {
  const { Link } = useUiKitComponents();
  return /* @__PURE__ */ jsx(Link, { href, ref, ...rest, children });
});
const DropdownItem = ({
  item,
  state
}) => {
  var _a, _b, _c, _d;
  const elementRef = useRef(null);
  const linkRef = useRef(null);
  const { menuItemProps } = useMenuItem(
    { key: item.key },
    state,
    ((_a = item.props) == null ? void 0 : _a.href) ? linkRef : elementRef
  );
  return ((_b = item.props) == null ? void 0 : _b.href) ? /* @__PURE__ */ jsx(
    LinkElementType,
    {
      ...menuItemProps,
      ref: linkRef,
      href: item.props.href,
      onClick: (_c = item.props) == null ? void 0 : _c.onClose,
      children: item.rendered
    }
  ) : /* @__PURE__ */ jsx(
    ElementType,
    {
      ...menuItemProps,
      ref: elementRef,
      onClick: (_d = item.props) == null ? void 0 : _d.onClose,
      children: item.rendered
    }
  );
};
const DropdownSection = (props) => {
  const { section, state, ...indentationProps } = props;
  const { itemProps, headingProps, groupProps } = useMenuSection({
    heading: section.rendered,
    "aria-label": section["aria-label"]
  });
  return /* @__PURE__ */ jsxs("li", { ...itemProps, children: [
    section.rendered && /* @__PURE__ */ jsx(
      Box,
      {
        className: `[font-size:var(--fontSize-desktop-small-body)] [font-weight:var(--fontWeight-desktop-semibold)] uppercase text-light-heading`,
        ...headingProps,
        ...indentationProps,
        style: {
          ...indentationProps.style
        },
        as: "span",
        children: section.rendered
      }
    ),
    /* @__PURE__ */ jsx("ul", { ...groupProps, className: "list-none", children: [...section.childNodes].map((node) => /* @__PURE__ */ jsx(DropdownItem, { item: node, state }, node.key)) })
  ] });
};
const DropdownTriggerElement = forwardRef(
  (props, ref) => {
    const localRef = useRef(null);
    const buttonRef = ref != null ? ref : localRef;
    const { buttonProps } = useButton(props, buttonRef);
    const [hasClicked, setHasClicked] = useState(false);
    let closeTimeout;
    useEffect(() => {
      if (!props.state.isOpen && hasClicked) {
        setHasClicked(false);
      }
    }, [props.state.isOpen]);
    const handleMouseEnter = () => {
      clearTimeout(closeTimeout);
      if (props.strategy !== "click") {
        props.state.open();
      }
    };
    const handleMouseLeave = () => {
      if (hasClicked) return;
      closeTimeout = setTimeout(() => {
        if (props.strategy !== "click") {
          props.state.close();
        }
      }, 250);
    };
    const handleClick = () => {
      if (!hasClicked) {
        setHasClicked(true);
        props.state.open();
      }
    };
    return React.cloneElement(props.triggerElement, {
      ...buttonProps,
      ref: buttonRef,
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
      onClick: props.strategy === "mixed" ? handleClick : buttonProps.onClick,
      children: /* @__PURE__ */ jsxs(Fragment, { children: [
        props.triggerElement.props.children,
        props.children
      ] })
    });
  }
);
const DropdownTrigger = ({
  isNonModal = true,
  ...props
}) => {
  var _a;
  const state = useMenuTriggerState(props);
  const ref = useRef(null);
  const { menuTriggerProps, menuProps } = useMenuTrigger({}, state, ref);
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    props.alwaysRenderMenu && /* @__PURE__ */ jsxs(Box, { position: { type: "absolute", left: "-9999px", top: "-9999px" }, children: [
      /* @__PURE__ */ jsx(Fragment, { children: props.triggerElement }),
      /* @__PURE__ */ jsx(Dropdown, { wrapper: props.dropdownWrapper, ...props, ...menuProps })
    ] }),
    /* @__PURE__ */ jsx(
      DropdownTriggerElement,
      {
        ...menuTriggerProps,
        strategy: props.strategy,
        triggerElement: props.triggerElement,
        ref,
        state,
        children: state.isOpen && /* @__PURE__ */ jsx(
          Popover,
          {
            ...props,
            state,
            triggerRef: ref,
            isNonModal,
            placement: (_a = props.placement) != null ? _a : "bottom start",
            offset: props.offset,
            variant: props.variant,
            children: /* @__PURE__ */ jsx(
              Dropdown,
              {
                wrapper: props.dropdownWrapper,
                ...props,
                ...menuProps
              }
            )
          }
        )
      }
    )
  ] });
};
const Dropdown = (props) => {
  var _a;
  const state = useTreeState(props);
  const ref = useRef(null);
  const { menuProps } = useMenu(props, state, ref);
  const Wrapper = (_a = props.wrapper) != null ? _a : DefaultWrapper;
  const resolvedProps = useResolvedAttributes(props);
  return /* @__PURE__ */ jsx("ul", { ...menuProps, ...resolvedProps, ref, children: /* @__PURE__ */ jsx(Wrapper, { children: [...state.collection].map((item) => {
    if (item.type === "section") {
      return /* @__PURE__ */ jsx(DropdownSection, { section: item, state }, item.key);
    } else {
      return /* @__PURE__ */ jsx(DropdownItem, { item, state }, item.key);
    }
  }) }) });
};
Dropdown.Item = Item;
Dropdown.Section = Section;
Dropdown.Trigger = DropdownTrigger;
export {
  Dropdown as D
};
